import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import type { Role } from '@botguide/shared';

import { AppShell } from '@/components';
import { CLIENT_DASHBOARD_PATTERN, ROUTES, SCREEN_ROLES, landingPathFor } from '@/lib/routes';
import { RequireAuth, RequireRole, useSession } from '@/lib/session';
import Capacity from '@/pages/Capacity';
import ClientDashboard from '@/pages/ClientDashboard';
import Forbidden from '@/pages/Forbidden';
import HoursReview from '@/pages/HoursReview';
import MyHours from '@/pages/MyHours';
import NotFound from '@/pages/NotFound';
import People from '@/pages/People';
import SignIn from '@/pages/SignIn';
import WorkBoard from '@/pages/WorkBoard';
import WorkItems from '@/pages/WorkItems';

/*
 * Every screen is a default export from its own file in `pages/`, takes no
 * props, and renders its own `<PageHeader>` - the shell is applied here by
 * `Screen` so no page has to remember to. The contract is `ScreenComponent`
 * in `src/lib/screens.ts`.
 */


/* -------------------------------------------------------------------------- */

/**
 * A signed-in screen: authenticated, role-checked, inside the shell.
 *
 * The guard is outside `AppShell` on purpose. A person who may not be here
 * should never see the navigation - which is a list of the screens that exist
 * - flash up before being redirected.
 */
function Screen({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  return (
    <RequireRole roles={roles}>
      <AppShell>{children}</AppShell>
    </RequireRole>
  );
}

/** `/` sends each role to the screen their role actually has data on. */
function Landing() {
  const { status, user } = useSession();

  if (status === 'loading') return null;
  if (!user) return <Navigate to={ROUTES.signIn} replace />;
  return <Navigate to={landingPathFor(user.role)} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path={ROUTES.signIn} element={<SignIn />} />

      <Route path="/" element={<Landing />} />

      {/* Delivery */}
      <Route
        path={ROUTES.board}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.board]!}>
            <WorkBoard />
          </Screen>
        }
      />
      <Route
        path={ROUTES.work}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.work]!}>
            <WorkItems />
          </Screen>
        }
      />
      <Route
        path={ROUTES.capacity}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.capacity]!}>
            <Capacity />
          </Screen>
        }
      />

      {/* Team */}
      <Route
        path={ROUTES.people}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.people]!}>
            <People />
          </Screen>
        }
      />
      <Route
        path={ROUTES.myHours}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.myHours]!}>
            <MyHours />
          </Screen>
        }
      />
      <Route
        path={ROUTES.hoursReview}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.hoursReview]!}>
            <HoursReview />
          </Screen>
        }
      />

      {/* Client-facing. The owner gets in so they can preview what is shown. */}
      <Route
        path={ROUTES.dashboard}
        element={
          <Screen roles={SCREEN_ROLES[ROUTES.dashboard]!}>
            <ClientDashboard />
          </Screen>
        }
      />
      <Route
        path={CLIENT_DASHBOARD_PATTERN}
        element={
          <Screen roles={SCREEN_ROLES[CLIENT_DASHBOARD_PATTERN]!}>
            <ClientDashboard />
          </Screen>
        }
      />

      <Route
        path={ROUTES.forbidden}
        element={
          <RequireAuth>
            <AppShell>
              <Forbidden />
            </AppShell>
          </RequireAuth>
        }
      />

      {/*
        The catch-all is inside the shell when signed in, so a mistyped URL
        still leaves the navigation to hand. RequireAuth sends everyone else
        to sign in, which is also the right answer for a stale bookmark.
      */}
      <Route
        path="*"
        element={
          <RequireAuth>
            <AppShell>
              <NotFound />
            </AppShell>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
